import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import PersonIcon from "@mui/icons-material/Person";

import PageHeader from "../../utils/PageHeader";

import menuConfig from "../../config/menuConfig";

// Change this according to your axios/api setup
import { api } from "../../component/constent";

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const [password, setPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const response = await api.get("/entry/my-profile");

      if (response.data?.success) {
        setProfile(response.data.data);

//console.log("User Profile Data:", response);

      } else {
        throw new Error(
          response.data?.message || "Unable to load profile"
        );
      }
    } catch (error) {
      console.error(error);

      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to load user profile",
      });
    } finally {
      setLoading(false);
    }
  };

  /*
   * Convert:
   *
   * F;G;L;M;O;S;
   *
   * into:
   *
   * EGA/ENGA Data
   * Insentive Data
   * POH Out-Turn
   * Update Labour Rate
   * Reports
   * ...
   */


  const rights = useMemo(() => {
  if (!profile?.loginRights) return [];

  // Permissions assigned to logged-in user
  const userPermissions = profile.loginRights
    .split(";")
    .map((permission) => permission.trim())
    .filter(Boolean);

  // Map permission code -> menu titles
  const permissionMap = new Map();

  const addPermission = (permission, title) => {
    if (!userPermissions.includes(permission)) {
      return;
    }

    if (!permissionMap.has(permission)) {
      permissionMap.set(permission, []);
    }

    const titles = permissionMap.get(permission);

    if (!titles.includes(title)) {
      titles.push(title);
    }
  };

  const walkMenu = (items) => {
    items.forEach((item) => {
      const children = item.children || [];

      /*
       * If children have permissions, use the child
       * permissions instead of the parent's permission.
       *
       * Example:
       *
       * Entry
       *   Time Office       E
       *   EGA/ENGA          F
       *   Incentive         G
       */
      const childrenHavePermissions = children.some(
        (child) => child.permission
      );

      if (children.length && childrenHavePermissions) {
        walkMenu(children);
        return;
      }

      /*
       * Parent has permission and children don't.
       *
       * Example:
       *
       * Reports             O
       *   Consignee List
       *   Rate List
       *   Monthly Reports
       */
      if (item.permission) {
        const permissions = Array.isArray(item.permission)
          ? item.permission
          : [item.permission];

        permissions.forEach((permission) => {
          addPermission(permission, item.title);
        });
      }

      /*
       * Parent doesn't have permission.
       * Check children anyway.
       */
      if (children.length && !item.permission) {
        walkMenu(children);
      }
    });
  };

  walkMenu(menuConfig);

  /*
   * Convert Map into array.
   *
   * One permission = one entry.
   */
  return Array.from(permissionMap.entries()).map(
    ([code, titles]) => ({
      code,
      titles,
    })
  );
}, [profile]);


  const handlePasswordChange = (event) => {
    const { name, value } = event.target;

    setPassword((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (!password.currentPassword) {
      setMessage({
        type: "error",
        text: "Please enter your current password.",
      });
      return;
    }

    if (!password.newPassword) {
      setMessage({
        type: "error",
        text: "Please enter your new password.",
      });
      return;
    }

    if (password.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "New password must be at least 6 characters.",
      });
      return;
    }

    if (password.newPassword !== password.confirmPassword) {
      setMessage({
        type: "error",
        text: "New password and confirm password do not match.",
      });
      return;
    }

    try {
      setPasswordLoading(true);

      const response = await api.put("/entry/my-password", password);

      if (response.data?.success) {
        setPassword({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        setPasswordOpen(false);

        setMessage({
          type: "success",
          text: "Password changed successfully.",
        });
      } else {
        throw new Error(
          response.data?.message || "Unable to change password"
        );
      }
    } catch (error) {
      console.error(error);

      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to change password.",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isActive =
    profile?.activestatus === 1 ||
    profile?.activestatus === "1" ||
    profile?.activestatus === "Active";

  const initials =
    profile?.loginName
      ?.split(" ")
      .map((x) => x.charAt(0))
      .slice(0, 2)
      .join("")
      .toUpperCase() ||
    profile?.loginUserName?.charAt(0)?.toUpperCase() ||
    "U";

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">
            Loading profile...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Unable to load user profile.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* PAGE HEADER */}

      <PageHeader
        title="My Profile"
        subtitle="View your account details, access rights and security settings"
        icon={AccountCircleIcon}
        statusChip={{
          label: isActive ? "Active Account" : "Inactive Account",
          color: isActive ?  "success" : "error" ,
        }}
        showRefresh
        onRefresh={loadProfile}
        refreshing={loading}
      />

      {/* GLOBAL MESSAGE */}

      {message.text && (
        <Alert
          severity={message.type || "info"}
          sx={{
            mt: 2,
            borderRadius: 2,
          }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "320px 1fr",
          },
          gap: 3,
          mt: 3,
        }}
      >
        {/* LEFT PROFILE CARD */}

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
            height: "fit-content",
          }}
        >
          <Box
            sx={{
              background:
                "linear-gradient(135deg,#0d47a1 0%,#1976d2 55%,#42a5f5 100%)",
              p: 3,
              textAlign: "center",
              color: "white",
            }}
          >
            <Avatar
              sx={{
                width: 90,
                height: 90,
                mx: "auto",
                mb: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                border: "3px solid rgba(255,255,255,0.7)",
                fontSize: 30,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>

            <Typography
              variant="h6"
              fontWeight={700}
            >
              {profile.loginName || "User"}
            </Typography>

            <Typography
              variant="body2"
              sx={{ opacity: 0.9 }}
            >
              @{profile.loginUserName}
            </Typography>

            <Chip
              size="small"
              icon={
                isActive ? (
                  <CheckCircleIcon />
                ) : (
                  <BlockIcon />
                )
              }
              label={isActive ? "Inactive" : "Active"}
              sx={{
                mt: 2,
                color: "white",
                borderColor: "rgba(255,255,255,.5)",
                backgroundColor: "rgba(255,255,255,.15)",
              }}
              variant="outlined"
            />
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              ACCOUNT SUMMARY
            </Typography>

            <Stack spacing={2}>
              
              <SummaryRow
                icon={<CalendarMonthIcon />}
                label="Created"
                value={formatDate(profile.createddate)}
              />

              <SummaryRow
                icon={<PersonIcon />}
                label="Created By"
                value={profile.createdby || "-"}
              />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<LockIcon />}
              onClick={() => {
                setMessage({ type: "", text: "" });
                setPasswordOpen(true);
              }}
              sx={{
                borderRadius: 2,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Change Password
            </Button>
          </Box>
        </Paper>

        {/* RIGHT CONTENT */}

        <Stack spacing={3}>
          {/* PERSONAL INFORMATION */}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <SectionHeader
              icon={<AccountCircleIcon />}
              title="Personal Information"
              subtitle="Your registered account information"
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: 2,
                mt: 3,
              }}
            >
              <ReadOnlyField
                label="Full Name"
                value={profile.loginName}
                icon={<PersonIcon />}
              />

              <ReadOnlyField
                label="Username"
                value={profile.loginUserName}
                icon={<BadgeIcon />}
              />

              <ReadOnlyField
                label="Email Address"
                value={profile["E-Mail"]}
                icon={<EmailIcon />}
              />

              <ReadOnlyField
                label="Mobile Number"
                value={profile.mobile}
                icon={<PhoneIcon />}
              />
            </Box>
          </Paper>

          {/* RIGHTS */}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <SectionHeader
              icon={<AdminPanelSettingsIcon />}
              title="My Access Rights"
              subtitle={`${rights.length} permission${
                rights.length === 1 ? "" : "s"
              } assigned to your account`}
            />

            {rights.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                }}
              >
                No additional menu permissions are assigned
                to your account.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 1.5,
                  mt: 3,
                }}
              >
                {rights.map((right) => (
  <Paper
    key={right.code}
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      transition: "0.2s",

      "&:hover": {
        borderColor: "primary.main",
        transform: "translateY(-1px)",
        boxShadow: 1,
      },
    }}
  >
    <Avatar
      sx={{
        width: 40,
        height: 40,
        bgcolor: "primary.50",
        color: "primary.main",
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {right.code}
    </Avatar>

    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
      >
        PERMISSION {right.code}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5 }}
      >
        {right.titles.map((title) => (
          <Chip
            key={title}
            label={title}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 1.5,
            }}
          />
        ))}
      </Stack>
    </Box>
  </Paper>
))}
              </Box>
            )}
          </Paper>

          {/* SECURITY */}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <SectionHeader
              icon={<LockIcon />}
              title="Security"
              subtitle="Manage your account password"
            />

            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography fontWeight={600}>
                  Account Password
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Change your password to keep your account secure.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setPasswordOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Change Password
              </Button>
            </Box>
          </Paper>
        </Stack>
      </Box>

      {/* CHANGE PASSWORD DIALOG */}

      <Dialog
        open={passwordOpen}
        onClose={() => {
          if (!passwordLoading) {
            setPasswordOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            pb: 1,
          }}
        >
          Change Password
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Enter your current password and choose a new password.
          </Typography>

          <Stack spacing={2}>
            <PasswordField
              label="Current Password"
              name="currentPassword"
              value={password.currentPassword}
              onChange={handlePasswordChange}
              show={showCurrent}
              onToggle={() => setShowCurrent((v) => !v)}
            />

            <PasswordField
              label="New Password"
              name="newPassword"
              value={password.newPassword}
              onChange={handlePasswordChange}
              show={showNew}
              onToggle={() => setShowNew((v) => !v)}
            />

            <PasswordField
              label="Confirm New Password"
              name="confirmPassword"
              value={password.confirmPassword}
              onChange={handlePasswordChange}
              show={showConfirm}
              onToggle={() => setShowConfirm((v) => !v)}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setPasswordOpen(false)}
            disabled={passwordLoading}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              passwordLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <LockIcon />
              )
            }
            onClick={handleChangePassword}
            disabled={passwordLoading}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {passwordLoading
              ? "Changing..."
              : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* -------------------------------------------------------
   SMALL REUSABLE COMPONENTS
------------------------------------------------------- */

const SectionHeader = ({
  icon,
  title,
  subtitle,
}) => {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
    >
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: "primary.50",
          color: "primary.main",
        }}
      >
        {icon}
      </Avatar>

      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
};

const SummaryRow = ({
  icon,
  label,
  value,
}) => {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
    >
      <Box
        sx={{
          color: "primary.main",
          display: "flex",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
        >
          {value || "-"}
        </Typography>
      </Box>
    </Stack>
  );
};

const ReadOnlyField = ({
  label,
  value,
  icon,
}) => {
  return (
    <TextField
      fullWidth
      label={label}
      value={value || ""}
      disabled
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {icon}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiInputBase-root": {
          borderRadius: 2,
          backgroundColor: "action.hover",
        },
      }}
    />
  );
};

const PasswordField = ({
  label,
  name,
  value,
  onChange,
  show,
  onToggle,
}) => {
  return (
    <TextField
      fullWidth
      type={show ? "text" : "password"}
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      autoComplete="new-password"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LockIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onToggle}
              edge="end"
              tabIndex={-1}
            >
              {show ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiInputBase-root": {
          borderRadius: 2,
        },
      }}
    />
  );
};

export default UserProfile;